import { useState, useEffect, useRef } from 'react';
import { fetchSubjects, saveSubject, saveLibrarySubjectPrefs, removeSubject } from './SubjectManipulation';
import getColors from '../Functions/getColors';
import { useNavigate } from 'react-router-dom';
import { fetchCollections } from '../Collections/CollectionManipulation';
import BackgroundButton from '../Elements/BackgroundButton';
import { useUser } from '../../UserContext';
import AddSubject from '../Subject/AddSubject';
import { X, Plus, MoreVertical, ArrowUpDown } from 'lucide-react';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';

function SubjectList({ isOpen, onClose, user, page = "practice" }) {
    const navigate = useNavigate();
    const [subjects, setSubjects] = useState([]);
    const [collections, setCollections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCollection, setSelectedCollection] = useState(null);
    const [isAddOpen, setIsAddOpen] = useState(false);
    const [editingSubject, setEditingSubject] = useState(null);
    const { theme, profile } = useUser();
    const { secondaryColor } = theme;
    const [confirmDeleteSubject, setConfirmDeleteSubject] = useState(null);
    const [sortBy, setSortBy] = useState('Most Cards');
    const PAGE_SIZE = 30;
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const selectRef = useRef(null);

    useBodyScrollLock(isOpen);

    const handleRemove = async (id) => {
      await removeSubject(id);
      const refreshed = await fetchSubjects(user, profile);
      setSubjects(refreshed);
    };

    useEffect(() => {
        const loadData = async () => {
            if (user?.id) {
                setLoading(true);
                const [subjectsData, collectionsData] = await Promise.all([
                    fetchSubjects(user, profile),
                    fetchCollections(user.id)
                ]);
                setSubjects(subjectsData);
                setCollections(collectionsData);
                setLoading(false);
            } else {
                setLoading(false);
            }
        };

        loadData();
    }, [user, profile]);

    useEffect(() => {
      if (isOpen) setVisibleCount(PAGE_SIZE);
    }, [isOpen, PAGE_SIZE]);

    const goToDashboard = () => {
        navigate('/dashboard');
    }

    if (!isOpen) return null;

    // Keep the modal chrome visible while subjects load (avoid bare "Loading...")
    const showSkeleton = loading;

    // Group subjects by collectionId
    const collectionsWithSubjects = collections.map((collection) => ({
        ...collection,
        subjects: subjects.filter(subject => subject.collection_id === collection.id)
    }));

    // Filter subjects without a collection
    const unassignedSubjects = subjects.filter(subject => subject.collection_id === null);

    // Combine collections and unassigned subjects
    const combinedList = [
        ...unassignedSubjects.map(subject => ({ ...subject, type: 'subject' })), // Mark subjects as type 'subject'
        ...collectionsWithSubjects.map(collection => ({ ...collection, type: 'collection' })) // Mark collections as type 'collection'
    ];

    // Sort the combined list based on the selected sort option
    const sortedCombinedList = combinedList.sort((a, b) => {
        switch (sortBy) {
            case 'Alphabetical':
                return a.name.localeCompare(b.name);
            case 'Most Cards':
                if (a.type === 'subject' && b.type === 'subject') {
                    return b.flashcard_count - a.flashcard_count;
                }
                if (a.type === 'collection' && b.type === 'collection') {
                    return b.subjects.length - a.subjects.length;
                }
                return a.type === 'subject' ? -1 : 1;
            case 'Date Created':
                return new Date(b.created_at) - new Date(a.created_at);
            default:
                return 0;
        }
    });

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-0 sm:p-6">
          <div className="bg-gradient-to-t from-black/30 via-black/15 to-transparent backdrop-blur-xl sm:rounded-xl p-6 w-full sm:w-4/5 max-w-lg h-full sm:h-[70%] max-h-[100dvh] sm:max-h-[90dvh] shadow-2xl shadow-black/30 border border-white/20 relative flex flex-col overflow-hidden">
            {selectedCollection ? (
              <CollectionView 
                collection={selectedCollection} 
                subjects={selectedCollection.subjects} 
                onBack={() => setSelectedCollection(null)}
                onClose={onClose}
                page={page}
                theme={theme}
                onEditSubject={(subj) => { setEditingSubject(subj); setIsAddOpen(true); }}
                onDeleteSubject={(subj) => setConfirmDeleteSubject(subj)}
              />
            ) : (
              <>
                <div className="flex-none">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-semibold mb-0 text-white/90">Subjects & Collections</h2>
                    <div className="flex gap-2">
                      <div className="relative">
                        <select
                          ref={selectRef}
                          value={sortBy}
                          onChange={(e) => setSortBy(e.target.value)}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10 text-base"
                          style={{ minWidth: '40px' }}
                        >
                          <option value="Most Cards">Most Cards</option>
                          <option value="Alphabetical">Alphabetical</option>
                          <option value="Date Created">Date Created</option>
                        </select>
                        <div className="relative z-0">
                          <BackgroundButton 
                            image={<ArrowUpDown/>} 
                            bgColor={'bg-blue-500 hover:bg-blue-400'} 
                          />
                        </div>
                      </div>
                      <BackgroundButton image={<Plus/>} bgColor={'bg-green-500 hover:bg-green-400'} onClick={() => { setEditingSubject(null); setIsAddOpen(true); }}/>
                      <BackgroundButton image={<X/>} bgColor={'bg-red-500 hover:bg-red-400'} onClick={onClose}/>
                    </div>
                  </div>
                </div>
      
                <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain pb-8">
                  {showSkeleton ? (
                    <div className="space-y-2 animate-pulse" aria-busy="true" aria-label="Loading subjects">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="w-[99%] h-16 rounded-xl bg-white/20" />
                      ))}
                    </div>
                  ) : subjects.length > 0 ? (
                    <>
                      {sortedCombinedList.slice(0, visibleCount).map((item) => {
                        if (item.type === 'subject') {
                          const isSharedViewer = item.permission === 'viewer' && !item.isFromDiscover;
                          const isContentReadOnly = item.isFromDiscover || isSharedViewer;
                          // Create requires edit access; practice/games allow library + shared viewers
                          if (page === 'create' && isContentReadOnly) {
                            return null;
                          }
                          return (
                            <SubjectRow
                              key={`subject-${item.id}`}
                              subject={item}
                              page={page}
                              onClose={onClose}
                              themeShadow={'background-shadow-new'}
                              onEdit={
                                isSharedViewer
                                  ? undefined
                                  : () => {
                                      setEditingSubject(item);
                                      setIsAddOpen(true);
                                    }
                              }
                              onDelete={
                                isContentReadOnly
                                  ? undefined
                                  : () => setConfirmDeleteSubject(item)
                              }
                            />
                          );
                        } else if (item.type === 'collection') {
                          return (
                            <CollectionRow
                              key={`collection-${item.id}`}
                              collection={item}
                              subjects={item.subjects}
                              onClick={() => setSelectedCollection(item)}
                              themeShadow={'background-shadow-new'}
                            />
                          );
                        }
                        return null;
                      })}

                      {visibleCount < sortedCombinedList.length && (
                        <div className="mt-6 flex justify-center">
                          <BackgroundButton
                            text="Load more"
                            bgColor={
                              theme
                                ? `${secondaryColor.bgClass} ${secondaryColor.hoverClass}`
                                : 'bg-purple-500 hover:bg-purple-400'
                            }
                            onClick={() =>
                              setVisibleCount((n) => Math.min(n + PAGE_SIZE, sortedCombinedList.length))
                            }
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center text-lg text-white/80 mt-10 flex flex-col items-center px-4">
                      <p className="mb-2 font-bold text-xl text-white">No subjects yet</p>
                      <p className="mb-4 text-sm text-white/60">
                        Create one here to jump straight into {page}.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-2 items-center">
                        <BackgroundButton
                          text="Create a subject"
                          onClick={() => { setEditingSubject(null); setIsAddOpen(true); }}
                          bgColor={theme ? `${secondaryColor.bgClass} ${secondaryColor.hoverClass}` : `bg-purple-500 hover:bg-purple-400`}
                        />
                        <BackgroundButton
                          text="Go to Dashboard"
                          onClick={goToDashboard}
                          bgColor="bg-gray-600 hover:bg-gray-500"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          <AddSubject
            isOpen={isAddOpen}
            onClose={() => setIsAddOpen(false)}
            onSave={async (subjectId, name, colourText, colourIntensity, upToIndex, collectionId, pinned) => {
              const existing = subjectId
                ? subjects.find((s) => s.id === subjectId) || editingSubject
                : null;

              if (existing?.isFromDiscover) {
                await saveLibrarySubjectPrefs(user.id, subjectId, {
                  colourText,
                  colourIntensity,
                  collectionId,
                  pinned: pinned ?? existing.pinned ?? false,
                });
              } else {
                const data = await saveSubject(
                  subjectId,
                  name,
                  colourText,
                  colourIntensity,
                  user.id,
                  upToIndex,
                  collectionId,
                  pinned ?? false
                );
                // New subject from empty picker → go straight to the destination page
                if (!subjectId && Array.isArray(data) && data[0]?.id) {
                  const created = data[0];
                  onClose();
                  navigate(`/${page}/${created.id}`, { state: { subject: created } });
                  return;
                }
              }
              // reload list
              const refreshed = await fetchSubjects(user, profile);
              setSubjects(refreshed);
              setIsAddOpen(false);
            }}
            subject={editingSubject}
            text={editingSubject ? 'Edit Subject' : 'Add New Subject'}
            user={user}
          />
          {confirmDeleteSubject && (
            <div className="absolute inset-0 flex justify-center items-center">
              <div className="bg-gradient-to-t from-black/30 via-black/15 to-transparent backdrop-blur-xl rounded-xl p-6 w-[80%] sm:w-1/2 max-w-md mx-auto shadow-2xl shadow-black/30 border border-white/20 z-50">
                <p className="text-white text-xl mb-4">Do you really want to delete "{confirmDeleteSubject.name}"?</p>
                <div className="flex justify-end space-x-4">
                  <BackgroundButton
                    text="Cancel"
                    bgColor="bg-gray-500 hover:bg-gray-400"
                    onClick={() => setConfirmDeleteSubject(null)}
                  />
                  <BackgroundButton
                    text="Delete"
                    bgColor="bg-red-500 hover:bg-red-400"
                    onClick={() => { handleRemove(confirmDeleteSubject.id); setConfirmDeleteSubject(null); }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      );
}

export default SubjectList;

function SubjectRow({ subject, page, onClose, themeShadow = 'background-shadow-new', onEdit, onDelete }) {
    const [menuOpen, setMenuOpen] = useState(false);
    const subjectCol = getColors([subject.colourText, subject.colourIntensity]);
    const navigate = useNavigate();

    const openSubject = () => {
      navigate(`/${page}/${subject.id}`, { state: { subject } });
      onClose();
    };

    const onRowKeyDown = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openSubject();
      }
    };

    return (
      <div className={`relative mb-2 w-[99%]`}>
        <div 
          role="button"
          tabIndex={0}
          aria-label={`Open ${subject.name}`}
          className={`${subjectCol.bgClass} ${themeShadow} w-full h-16 flex justify-between items-center text-white font-bold text-xl px-2 rounded-xl cursor-pointer background-hover`}
          onClick={openSubject}
          onKeyDown={onRowKeyDown}
        >
          <div className="truncate w-full">
            <p className="text-2xl">{subject.name}</p>
            <p className="text-lg font-normal">{subject.flashcard_count} {subject.flashcard_count === 1 ? "card" : "cards"}</p>
          </div>
          {(onEdit || onDelete) && (
            <button
              onClick={(e) => { e.stopPropagation(); setMenuOpen(o => !o); }}
              className="p-2 hover:bg-gray-700 rounded-full"
            >
              <MoreVertical className="text-white" />
            </button>
          )}
        </div>
        {menuOpen && (onEdit || onDelete) && (
          <div className="absolute right-0 top-full mt-1 bg-gradient-to-t from-black/30 via-black/15 to-transparent backdrop-blur-xl rounded-xl p-2 w-40 border border-white/20 shadow-2xl shadow-black/30 z-50">
            <button
              onClick={() => setMenuOpen(false)}
              className="block w-full text-left px-4 py-2 text-blue-600 rounded-md hover:bg-gray-200/20 dark:hover:bg-gray-700/20"
            >
              Cancel
            </button>
            {onEdit && (
              <button
                onClick={() => { setMenuOpen(false); onEdit(); }}
                className="block w-full text-left px-4 py-2 rounded-md hover:bg-gray-200/20 dark:hover:bg-gray-700/20"
              >
                Edit
              </button>
            )}
            {onDelete && (
              <button
                onClick={() => { setMenuOpen(false); onDelete(); }}
                className="block w-full text-left px-4 py-2 text-red-600 rounded-md hover:bg-gray-200/20 dark:hover:bg-gray-700/20"
              >
                Delete
              </button>
            )}
          </div>
        )}
      </div>
    );
}

function CollectionRow({ collection, subjects, onClick, themeShadow = 'background-shadow-new' }) {

    const chev = (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="3" stroke="currentColor" className="size-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
        </svg>
    );

    const onKeyDown = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick();
      }
    };

    return (
        <div 
            key={collection.id}
            role="button"
            tabIndex={0}
            aria-label={`Open collection ${collection.name}`}
            className={`bg-[color-mix(in_srgb,var(--theme-border-color)_80%,black_20%)] w-[99%] h-16 mb-2 flex justify-between items-center text-white font-bold text-xl pl-2 pr-1 rounded-xl cursor-pointer ${themeShadow} background-hover`}
            onClick={onClick}
            onKeyDown={onKeyDown}
        >            <div>
                <p className="text-2xl">{collection.name}</p>
                <p className="text-lg font-normal">{subjects.length} {subjects.length === 1 ? "subject" : "subjects"}</p>
            </div>
            {chev}
        </div>
    );
}

function CollectionView({ collection, subjects, onBack, onClose, page, theme, onEditSubject, onDeleteSubject }) {
    const [sortBy, setSortBy] = useState('Most Cards');
    const selectRef = useRef(null);

    const chev = (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="3" stroke="currentColor" className="size-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
        </svg>
    );

    // Sort subjects based on the selected sort option
    const sortedSubjects = [...subjects].sort((a, b) => {
        switch (sortBy) {
            case 'Alphabetical':
                return a.name.localeCompare(b.name);
            case 'Most Cards':
                return b.flashcard_count - a.flashcard_count;
            case 'Date Created':
                return new Date(b.created_at) - new Date(a.created_at);
            default:
                return 0;
        }
    });

    return (
        <div className="w-full h-full flex flex-col">
            <div className="flex-none">
                <div className="flex justify-between items-center mb-4">
                    <BackgroundButton text={collection.name} image={chev} flip onClick={onBack} bgColor={'bg-green-500 hover:bg-green-400'}/>
                    <div className="flex gap-2">
                        <div className="relative">
                            <select
                                ref={selectRef}
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10 text-base"
                                style={{ minWidth: '40px' }}
                            >
                                <option value="Most Cards">Most Cards</option>
                                <option value="Alphabetical">Alphabetical</option>
                                <option value="Date Created">Date Created</option>
                            </select>
                            <div className="relative z-0">
                                <BackgroundButton 
                                    image={<ArrowUpDown/>} 
                                    bgColor={'bg-blue-500 hover:bg-blue-400'} 
                                />
                            </div>
                        </div>
                        <BackgroundButton image={<X/>} bgColor={'bg-red-500 hover:bg-red-400'} onClick={onClose}/>
                    </div>
                </div>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain pb-8">
                {sortedSubjects.length > 0 ? (
                    sortedSubjects.map((subject) => {
                        const isSharedViewer = subject.permission === 'viewer' && !subject.isFromDiscover;
                        const isContentReadOnly = subject.isFromDiscover || isSharedViewer;
                        if (page === 'create' && isContentReadOnly) {
                            return null;
                        }
                        return (
                        <SubjectRow
                            key={subject.id}
                            subject={subject}
                            onClose={onClose}
                            page={page}
                            themeShadow={'background-shadow-new'}
                            onEdit={
                              isSharedViewer
                                ? undefined
                                : () => onEditSubject(subject)
                            }
                            onDelete={
                              isContentReadOnly
                                ? undefined
                                : () => onDeleteSubject(subject)
                            }
                        />
                        );
                    })
                ) : (
                    <p>No subjects in this collection.</p>
                )}
            </div>
        </div>
    );
}